import React, {useEffect, useState} from 'react';
import {Col, Container, Row, Modal, Button, Form, InputGroup, Spinner, Card, Tooltip, Navbar, Nav, OverlayTrigger} from 'react-bootstrap';
import {BsFillPersonPlusFill, BsPeopleFill, BsPersonSquare} from "react-icons/bs";
import moment from "moment/moment";

import {UsersService} from './UsersService';
import UsersTable from '../../Components/UsersTable/UsersTable';


export default function UsersPage() {
    const usersService = new UsersService(process.env.REACT_APP_SUPPORT_DASHBOARD_USERS_URL);

    const ActionType = {
        DoNothing: 'DoNothing',
        AddRegisteredUser: 'AddRegisteredUser',
        ViewAsUnregisteredUser: 'ViewAsUnregisteredUser'
    };

    const newUserButtonStyle = {color: 'white', margin: '5px'};
    const verticalLineStyle = {width: '1px', height: '36px', float: 'left', backgroundColor: 'rgba(0,0,0,.5)', margin: '5px'};
    const viewAsUserIdStyle = {textAlign: 'center', width: '6em'};

    const [users, setUsers] = useState([]);
    const [viewAsUserId, setViewAsUserId] = useState('');

    const [showModal, setShowModal] = useState(false);
    const [findUserTitle, setFindUserTitle] = useState('');
    const [findUser, setFindUser] = useState(false);

    const [userId, setUserId] = useState('');
    const [userName, setUserName] = useState('');
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('');
    const [creationTime, setCreationTime] = useState('');
    const [actionType, setActionType] = useState(ActionType.DoNothing);

    const loadUsers = () => {
        usersService.getAllUsers()
            .then(result => {
                setUsers(result);
                setViewAsUserId(usersService.getViewAsUser());
            });
    }

    useEffect(() => {
        loadUsers();
    }, [])

    const onCancel = () => {
        hideModal();
    }

    const onAddUser = async (userIdToAdd) => {
        hideModal();

        if (!userIdToAdd)
            return;

        switch (actionType) {
            case ActionType.ViewAsUnregisteredUser:
                viewAsUser(userId);
                break;

            default: // ActionType.AddRegisteredUser
                const created = await usersService.addNewUser(userIdToAdd);
                if (created) {
                    loadUsers();
                }
                break;
        }
    }

    const showModalWindow = (title) => {
        setUserId('');
        setFindUserTitle(title);
        clearState();
        setShowModal(true);
    }

    const hideModal = () => {
        setShowModal(false);
    }

    const clearState = () => {
        setUserId('');
        setUserName('');
        setEmail('');
        setRole('');
        setCreationTime('');
    }

    const onFindUser = async (userIdToFind) => {
        if (!userIdToFind)
            return;

        clearState();
        setFindUser(true);

        const foundUser = await usersService.findUser(userIdToFind);

        setFindUser(false);
        if (!foundUser)
            return;

        setUserId(foundUser.userId);
        setUserName(foundUser.fullName);
        setEmail(foundUser.email);
        setRole(foundUser.role);
        setCreationTime(foundUser.creationTime);

        setViewAsUserId(foundUser.userId);
    }

    const onAddNewRegisteredUser = () => {
        setActionType(ActionType.AddRegisteredUser);
        showModalWindow('Add new registered user');
    }

    const onViewAsUnregisteredUser = () => {
        setActionType(ActionType.ViewAsUnregisteredUser);
        showModalWindow('View reports as this unregistered user');
    }

    const onResetPassword = async (selectedUserId) => {
        const accepted = await usersService.resetPassword(selectedUserId);
        if (accepted) {
            loadUsers();
        }
    }

    const onRemoveUser = async (selectedUserId) => {
        const accepted = await usersService.removeUser(selectedUserId);
        if (accepted) {
            loadUsers();
        }
    }

    const viewAsUser = (selectedUserId) => {
        usersService.setViewAsUser(selectedUserId);
        setViewAsUserId(selectedUserId);
        loadUsers();
    }

    const onViewAsUser = (selectedUserId) => {
        viewAsUser(selectedUserId);
    }

    return (
        <Container fluid>
            <br/>
            <Col md={{offset: 1, span: 10}}>
                <Row>
                    <Col>
                        <Navbar bg="light">
                            <Container fluid>
                                <Navbar.Brand><BsPersonSquare/>{' '}User Management</Navbar.Brand>
                                <Navbar.Collapse className={'justify-content-end'}>
                                    <Nav>
                                        <OverlayTrigger
                                            overlay={
                                                <Tooltip>Add new registered user</Tooltip>
                                            }>
                                            <Button style={newUserButtonStyle} variant="info" onClick={onAddNewRegisteredUser}>
                                                <BsFillPersonPlusFill/>
                                            </Button>
                                        </OverlayTrigger>
                                        <hr style={verticalLineStyle}/>
                                        <OverlayTrigger
                                            overlay={
                                                <Tooltip>View reports as this unregistered user</Tooltip>
                                            }>
                                            <Button style={newUserButtonStyle} variant="warning" onClick={onViewAsUnregisteredUser}>
                                                <BsPeopleFill/>
                                            </Button>
                                        </OverlayTrigger>
                                    </Nav>
                                    <Nav>
                                        <InputGroup>
                                            <Form.Control value={viewAsUserId} style={viewAsUserIdStyle} readOnly/>
                                        </InputGroup>
                                    </Nav>
                                </Navbar.Collapse>
                            </Container>
                        </Navbar>
                    </Col>
                </Row>
                <br/>
                <Row>
                    <Col>
                        <Modal show={showModal} onHide={onCancel}
                               backdrop="static" size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
                            <Modal.Header closeButton>
                                <Modal.Title>{findUserTitle}</Modal.Title>
                            </Modal.Header>

                            <Modal.Body>
                                <Row>
                                    <Col md={{span: 4}}>
                                        <Form.Label>User ID</Form.Label>
                                        <InputGroup className="mb-3">
                                            <Form.Control value={userId} type="text"
                                                          onChange={e => {
                                                              e.preventDefault();
                                                              setUserId(e.target.value);
                                                          }}/>
                                            <Button variant="outline-secondary" onClick={() => onFindUser(userId)} disabled={userId.length === 0}>Find...</Button>
                                        </InputGroup>
                                    </Col>
                                    <Col>
                                        <Spinner animation="border" variant="primary" hidden={!findUser}/>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Label>Full Name</Form.Label>
                                        <Form.Control value={userName} disabled/>
                                    </Col>
                                    <Col>
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control value={email} type="email" disabled/>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Label>Role</Form.Label>
                                        <Form.Control value={role} disabled/>
                                    </Col>
                                    <Col>
                                        <Form.Label>Creation Time</Form.Label>
                                        <Form.Control value={creationTime ? moment(creationTime).format('lll') : ''} disabled/>
                                    </Col>
                                </Row>
                            </Modal.Body>

                            <Modal.Footer>
                                <Button variant="primary" onClick={() => onAddUser(userId)} disabled={!userId}>Add</Button>
                                <Button variant="secondary" onClick={onCancel}>Cancel</Button>
                            </Modal.Footer>
                        </Modal>

                        <Card>
                            <Card.Header>Registered Users</Card.Header>
                            <Card.Body>
                                <UsersTable data={users}
                                            removeUser={onRemoveUser}
                                            resetPassword={onResetPassword}
                                            viewAsUser={onViewAsUser}
                                />
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Col>
        </Container>)
}